import classNames from "classnames";
import * as Select from "@radix-ui/react-select";
import {
  Popover,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
} from "~/components/shared/PopoverNext";
import Icon from "~/components/shared/Icon";
import { useTrack } from "~/components/build/Show/lib/useTrack";

import {
  DisplayMode,
  useFilterStore,
  useStateCounts,
} from "~/components/build/Show/lib/useFilterStore";
import { FilterByOption } from "~/components/build/Show/lib/filterConstants";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import Count from "~/components/build/Show/components/Count";

const options = {
  [DisplayMode.Pipeline]: "None",
  [DisplayMode.State]: "State",
};

const displayModeOrder = [DisplayMode.Pipeline, DisplayMode.State];

export default function DisplayModeDropdown() {
  const displayMode = useFilterStore((state) => state.displayMode);
  const setDisplayMode = useFilterStore((state) => state.setDisplayMode);
  const isDisplayOptionsOpen = useBuildStore((state) => state.isDisplayOptionsOpen);
  const setDisplayOptionsOpen = useBuildStore((state) => state.setDisplayOptionsOpen);
  const showSkippedSteps = useBuildPreferencesStore((state) => state.showSkippedSteps);
  const setShowSkippedSteps = useBuildPreferencesStore((state) => state.setShowSkippedSteps);
  const filterBy = useFilterStore((state) => state.filterBy);
  const setFilterBy = useFilterStore((state) => state.setFilterBy);
  const counts = useStateCounts();
  const skippedCount = counts[FilterByOption.Ignored];
  const track = useTrack();

  // When hiding skipped steps, also clear the skipped filter so users
  // don't end up with a hidden filter option they can't deselect.
  function toggleSkippedSteps() {
    if (showSkippedSteps) {
      setShowSkippedSteps(false);
      setFilterBy(filterBy.filter((filter) => filter !== FilterByOption.Ignored));
    } else {
      setShowSkippedSteps(true);
    }
  }

  return (
    <Popover open={isDisplayOptionsOpen} onOpenChange={setDisplayOptionsOpen}>
      <div className="flex">
        <PopoverTrigger asChild={true}>
          <button
            data-testid="display-mode-dropdown"
            className={classNames(
              "text-charcoal-700 shadow-xs group inline-flex h-7 max-w-full items-center gap-1 self-stretch rounded-md border border-slate-400 bg-[#F8FAFC] px-2 py-1 transition-colors hover:text-purple-600 focus:z-10",
            )}
          >
            <Icon icon="heroicons/16/solid/adjustments-horizontal" className="h-3 w-3 shrink-0" />
            <span className="max-w-full truncate">Display</span>
          </button>
        </PopoverTrigger>
      </div>

      <PopoverPortal>
        <PopoverContent
          side="bottom"
          align="end"
          alignOffset={1}
          size={null}
          className="w-max min-w-[280px] p-3"
          data-testid="display-mode-dropdown-content"
        >
          <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2">
            {/* Group by */}
            <span className="text-charcoal-700 text-sm">Group by</span>
            <Select.Root
              value={displayMode ?? undefined}
              onValueChange={(value) => {
                track(`Group Sidebar Steps`, { group_by: value });
                setDisplayMode(value as DisplayMode);
              }}
            >
              <Select.Trigger
                aria-label="Group by"
                className="no-title-tooltip inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-gray-400 bg-white px-3 py-0.5 text-sm outline-none"
              >
                <Select.Value />
                <Icon icon="down-triangle" className="size-[7px] shrink-0" />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  className="z-50 min-w-[120px]"
                  position="popper"
                  sideOffset={4}
                  align="end"
                >
                  <Select.Viewport className="rounded-md border border-gray-300 bg-white py-0.5 shadow-md">
                    {displayModeOrder.map((option) => (
                      <Select.Item
                        key={option}
                        value={option}
                        className="cursor-pointer px-3 py-1.5 text-left text-sm font-medium outline-none data-[highlighted]:bg-gray-100 data-[state=checked]:bg-purple-100 data-[highlighted]:text-purple-600 data-[state=checked]:text-purple-600"
                      >
                        <Select.ItemText>{options[option]}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>

            <hr className="col-span-2 my-1 border-gray-300" />

            {/* Toggle options */}
            <span className="text-charcoal-700 flex items-center gap-1.5 whitespace-nowrap text-sm">
              Show skipped steps
              {skippedCount > 0 && <Count>{skippedCount}</Count>}
            </span>
            <button
              role="switch"
              aria-label="Show skipped steps"
              aria-checked={showSkippedSteps}
              onClick={toggleSkippedSteps}
              className={classNames(
                "no-title-tooltip relative inline-flex h-4 w-7 shrink-0 cursor-pointer justify-self-end rounded-full border-2 border-transparent transition-colors",
                showSkippedSteps ? "bg-purple-600" : "bg-gray-300",
              )}
            >
              <span
                className={classNames(
                  "pointer-events-none inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform",
                  showSkippedSteps ? "translate-x-3" : "translate-x-0",
                )}
              />
            </button>
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}
